import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CircleCheck,
  ExternalLink,
  Receipt,
  RotateCcw,
  Search,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/invoicesData';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_BADGE = {
  paid:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  overdue:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  due:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  upcoming: 'bg-[hsl(var(--muted))] text-foreground border-border',
};

const FilterTabs = ({ value, onChange, counts }) => {
  const { t } = useTranslation();
  return (
    <Tabs value={value} onValueChange={onChange} className="w-full sm:w-auto">
      <TabsList
        className="bg-[hsl(var(--muted))] border border-border w-full sm:w-auto overflow-x-auto no-scrollbar"
        data-testid="invoices-status-tabs"
      >
        <TabsTrigger value="all" className="text-xs uppercase tracking-[0.16em]">
          {counts.all
            ? t('invoices.filters.all_count', { count: counts.all })
            : t('invoices.filters.all')}
        </TabsTrigger>
        <TabsTrigger value="overdue" className="text-xs uppercase tracking-[0.16em]">
          {counts.overdue
            ? t('invoices.filters.overdue_count', { count: counts.overdue })
            : t('invoices.filters.overdue')}
        </TabsTrigger>
        <TabsTrigger value="due" className="text-xs uppercase tracking-[0.16em]">
          {counts.due
            ? t('invoices.filters.due_count', { count: counts.due })
            : t('invoices.filters.due')}
        </TabsTrigger>
        <TabsTrigger value="upcoming" className="text-xs uppercase tracking-[0.16em]">
          {counts.upcoming
            ? t('invoices.filters.upcoming_count', { count: counts.upcoming })
            : t('invoices.filters.upcoming')}
        </TabsTrigger>
        <TabsTrigger value="paid" className="text-xs uppercase tracking-[0.16em]">
          {counts.paid
            ? t('invoices.filters.paid_count', { count: counts.paid })
            : t('invoices.filters.paid')}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
};

const InvoiceRow = ({ row, onOpenJob, onMarkPaid, onMarkUnpaid, busy }) => {
  const { t } = useTranslation();
  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.upcoming;
  const STATUS_LABEL = {
    paid: t('invoices.status.paid'),
    overdue: t('invoices.status.overdue'),
    due: t('invoices.status.due'),
    upcoming: t('invoices.status.upcoming'),
  };
  const label = STATUS_LABEL[row.status] || STATUS_LABEL.upcoming;
  const aging =
    row.status === 'overdue' && row.days_past_due
      ? t('invoices.table.aging_past_due', { days: row.days_past_due })
      : row.status === 'due' && row.days_until_due != null
      ? t('invoices.table.aging_in_days', { days: row.days_until_due })
      : '';
  return (
    <div
      className="grid grid-cols-12 sm:grid-cols-[minmax(0,2.2fr)_minmax(84px,1fr)_minmax(76px,1fr)_minmax(96px,1fr)_10.5rem] gap-2 items-center px-3 py-3 border-b border-border hover:bg-[hsl(var(--muted))]/30 transition-colors"
      data-testid={`invoice-line-${row.row_key}`}
    >
      <div className="col-span-12 sm:col-span-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {row.job_name}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {row.client || t('invoices.table.no_client')}
          {row.desc ? ` · ${row.desc}` : ''}
        </div>
      </div>
      <div className="col-span-4 sm:col-span-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('invoices.table.due')}
        </div>
        <div className="text-sm text-foreground">{row.due || t('common.tbd')}</div>
        {aging ? (
          <div
            className={cn(
              'text-[11px] mt-0.5',
              row.status === 'overdue'
                ? 'text-[hsl(var(--destructive))]'
                : 'text-[hsl(var(--warning))]'
            )}
          >
            {aging}
          </div>
        ) : null}
      </div>
      <div className="col-span-4 sm:col-span-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('invoices.table.amount')}
        </div>
        <div className="text-sm font-semibold text-foreground tabular-nums">
          {formatUsd(row.amount)}
        </div>
      </div>
      <div className="col-span-4 sm:col-span-1 flex justify-end sm:justify-start">
        <Badge
          variant="outline"
          className={cn('uppercase text-[10px] tracking-[0.16em]', badge)}
          data-testid={`invoice-row-status-${row.row_key}`}
        >
          {label}
        </Badge>
      </div>
      <div className="col-span-12 sm:col-span-1 flex items-center justify-end gap-2">
        {row.status === 'paid' ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onMarkUnpaid(row)}
            disabled={busy}
            className="h-8 border-border text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] uppercase tracking-[0.16em] text-[10px]"
            data-testid={`invoice-row-unmark-${row.row_key}`}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RotateCcw size={12} className="mr-1.5" />
            )}
            {t('invoices.table.undo')}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => onMarkPaid(row)}
            disabled={busy}
            className="h-8 bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/30 uppercase tracking-[0.16em] text-[10px]"
            data-testid={`invoice-row-mark-paid-${row.row_key}`}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <CircleCheck size={12} className="mr-1.5" />
            )}
            {t('invoices.mark_paid')}
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onOpenJob(row)}
          className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
          aria-label={t('invoices.table.open_job_aria')}
          data-testid={`invoice-row-open-job-${row.row_key}`}
        >
          <ExternalLink size={14} />
        </Button>
      </div>
    </div>
  );
};

const TableSkeleton = () => (
  <div className="divide-y divide-border" data-testid="invoices-skeleton">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="px-3 py-4 flex items-center gap-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-24 ml-auto" />
      </div>
    ))}
  </div>
);

export const InvoicesTable = ({
  rows,
  loading,
  statusFilter,
  onStatusFilter,
  search,
  onSearch,
  sort,
  onSort,
  counts,
  onOpenJob,
  onMarkPaid,
  onMarkUnpaid,
  busyRowId,
}) => {
  const { t } = useTranslation();
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="invoices-table-card"
    >
      <CardContent className="p-3 sm:p-4">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3">
          <FilterTabs value={statusFilter} onChange={onStatusFilter} counts={counts} />
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={14}
              />
              <Input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t('invoices.table.search_placeholder')}
                className="pl-8 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="invoices-search"
              />
            </div>
            <Select value={sort} onValueChange={onSort}>
              <SelectTrigger
                className="h-9 w-[180px] bg-[hsl(var(--muted))] border-border"
                data-testid="invoices-sort"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border">
                <SelectItem value="due_soonest">
                  {t('invoices.table.sort_due_soonest')}
                </SelectItem>
                <SelectItem value="due_latest">
                  {t('invoices.table.sort_due_latest')}
                </SelectItem>
                <SelectItem value="amount_high">
                  {t('invoices.table.sort_amount_high')}
                </SelectItem>
                <SelectItem value="amount_low">
                  {t('invoices.table.sort_amount_low')}
                </SelectItem>
                <SelectItem value="job_az">
                  {t('invoices.table.sort_job_az')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table header (desktop only) */}
        <div className="hidden sm:grid sm:grid-cols-[minmax(0,2.2fr)_minmax(84px,1fr)_minmax(76px,1fr)_minmax(96px,1fr)_10.5rem] gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span>{t('invoices.table.job_client')}</span>
          <span>{t('invoices.table.due')}</span>
          <span>{t('invoices.table.amount')}</span>
          <span>{t('invoices.table.status')}</span>
          <span className="text-right">{t('invoices.table.actions')}</span>
        </div>

        {/* Body */}
        {loading ? (
          <TableSkeleton />
        ) : rows.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2"
            data-testid="invoices-empty"
          >
            <Receipt size={20} className="text-muted-foreground/70" />
            {t('invoices.empty')}
          </div>
        ) : (
          <div data-testid="invoices-list">
            {rows.map((row) => (
              <InvoiceRow
                key={row.row_key}
                row={row}
                onOpenJob={onOpenJob}
                onMarkPaid={onMarkPaid}
                onMarkUnpaid={onMarkUnpaid}
                busy={busyRowId === row.row_key}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoicesTable;
